import { computeBasePoints, levelForPoints } from './scoring.js';
import { currentLocale, tr } from '../services/i18n.js';

export function buildLeaderboard({
  profiles = [],
  debriefs = [],
  comments = [],
  reactions = [],
  quizEntries = [],
  filterUserIds = null,
  competitionId = null
} = {}) {
  const allowed = filterUserIds ? new Set(filterUserIds) : null;
  const rows = new Map();

  profiles.forEach((profile) => {
    if (!profile?.id) return;
    if (allowed && !allowed.has(profile.id)) return;
    rows.set(profile.id, {
      user_id: profile.id,
      pseudo: profile.pseudo || tr('Joueur', 'Player'),
      avatar_url: profile.avatar_url || '',
      avatar_emoji: profile.avatar_emoji || '',
      debriefs: 0,
      comments: 0,
      reactions: 0,
      quizzes: 0,
      points: 0,
      level: 1
    });
  });

  function row(userId) {
    if (!userId) return null;
    if (allowed && !allowed.has(userId)) return null;
    if (!rows.has(userId)) {
      rows.set(userId, {
        user_id: userId,
        pseudo: tr('Joueur', 'Player'),
        avatar_url: '',
        avatar_emoji: '',
        debriefs: 0,
        comments: 0,
        reactions: 0,
        quizzes: 0,
        points: 0,
        level: 1
      });
    }
    return rows.get(userId);
  }

  const debriefCompetitionById = new Map();
  debriefs.forEach((d) => {
    if (!d?.id) return;
    debriefCompetitionById.set(d.id, d.competition_id || null);
    if (competitionId && d.competition_id !== competitionId) return;
    const r = row(d.user_id);
    if (r) r.debriefs += 1;
  });

  comments.forEach((c) => {
    if (competitionId && debriefCompetitionById.get(c.debrief_id) !== competitionId) return;
    const r = row(c.user_id);
    if (r) r.comments += 1;
  });

  reactions.forEach((reaction) => {
    if (competitionId && debriefCompetitionById.get(reaction.debrief_id) !== competitionId) return;
    const r = row(reaction.user_id);
    if (r) r.reactions += 1;
  });

  quizEntries.forEach((entry) => {
    if (competitionId && entry.competition_id !== competitionId) return;
    const r = row(entry.user_id);
    if (r) r.quizzes += 1;
  });

  return [...rows.values()]
    .map((r) => {
      const points = computeBasePoints(r);
      return { ...r, points, level: levelForPoints(points) };
    })
    .filter((r) => r.points > 0 || !allowed)
    .sort((a, b) => b.points - a.points || a.pseudo.localeCompare(b.pseudo, currentLocale()));
}

export function collectLeaderboardUserIds({ debriefs = [], comments = [], reactions = [], quizEntries = [] } = {}) {
  const ids = new Set();
  debriefs.forEach((entry) => { if (entry?.user_id) ids.add(entry.user_id); });
  comments.forEach((entry) => { if (entry?.user_id) ids.add(entry.user_id); });
  reactions.forEach((entry) => { if (entry?.user_id) ids.add(entry.user_id); });
  quizEntries.forEach((entry) => { if (entry?.user_id) ids.add(entry.user_id); });
  return [...ids];
}

export function mergeProfilesById(...groups) {
  const byId = new Map();
  groups.flat().forEach((profile) => {
    if (!profile?.id) return;
    byId.set(profile.id, { ...(byId.get(profile.id) || {}), ...profile });
  });
  return [...byId.values()];
}

export function rankLabel(index) {
  if (index === 0) return 'Champion';
  if (index === 1) return 'Challenger';
  if (index === 2) return 'Podium';
  return tr('Poursuite', 'Chasing pack');
}
